import { useState, useMemo, useCallback } from "react";
import { View, Text, ScrollView, Pressable, Platform } from "react-native";
import { ChevronLeft, Stethoscope } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { getBlocksByKind, type ContentBlock } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../onboarding/theme";
import type { ChatStackParamList } from "../../types";
import { MoonFilterChip, MoonSearchInput } from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { useSmartBack, getBackLabel } from "../../hooks/useSmartBack";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// Maps the raw category value coming from CMS to a stable i18n key.
const CATEGORY_I18N_KEYS: Record<string, string> = {
  reproductive: "reproductive",
  endocrine: "endocrine",
  autoimmune: "autoimmune",
  structural: "structural",
  genetic: "genetic",
  "male-factor": "male_factor",
};

type Props = NativeStackScreenProps<ChatStackParamList, "Conditions">;

export default function ConditionsScreen({ route, navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ConditionsScreen");
  const returnTab = route.params?.returnTab;
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Learn",
  });
  const backLabel =
    returnTab && returnTab !== "Learn"
      ? getBackLabel(returnTab, "Learn", t("you.shared.back"))
      : t("learn.back_to_learn");
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const diagnoses = useMemo(() => getBlocksByKind("condition"), []);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const dx of diagnoses) {
      if (dx.body.category) cats.add(dx.body.category);
    }
    return Array.from(cats).sort();
  }, [diagnoses]);

  const filtered = useMemo(() => {
    let result = diagnoses;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.body.conditionName.toLowerCase().includes(q) ||
          d.body.description.toLowerCase().includes(q) ||
          d.body.category.toLowerCase().includes(q),
      );
    }

    if (activeCategory) {
      result = result.filter((d) => d.body.category === activeCategory);
    }

    result.sort((a, b) => a.title.localeCompare(b.title));
    return result;
  }, [diagnoses, search, activeCategory]);

  const onItemPress = useCallback(
    (item: ContentBlock<"condition">) => {
      // Condition titles are authored catalogue copy, not patient data.
      capturePress(pressLabel(item.title), "learn_condition_card");
      navigation.navigate("ConditionDetail", { conditionId: item.id });
    },
    [capturePress, navigation],
  );

  const rows = useMemo(() => {
    const result: ContentBlock<"condition">[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      result.push(filtered.slice(i, i + 2));
    }
    return result;
  }, [filtered]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <Pressable
          onPress={() => {
            // backLabel is translated and varies with the origin tab.
            capturePress(pressLabel(backLabel), "learn_encyclopedia_back");
            handleBack();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {backLabel}
          </Text>
        </Pressable>

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 38,
            fontWeight: "700",
            color: "#3B1A08",
            lineHeight: 42,
            letterSpacing: -0.5,
            ...(Platform.OS === "web" && {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
              backgroundSize: "200px 200px, 100% 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }),
          }}
        >
          {t("learn.encyclopedia_screens.conditions.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("learn.encyclopedia_screens.conditions.subtitle")}
        </Text>

        {/* Search bar */}
        <MoonSearchInput
          value={search}
          onChangeText={setSearch}
          placeholder={t(
            "learn.encyclopedia_screens.conditions.search_placeholder",
          )}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        {/* Category filter chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingVertical: 14,
          }}
        >
          <MoonFilterChip
            label={t("learn.encyclopedia_screens.all")}
            selected={!activeCategory}
            onPress={() => setActiveCategory(null)}
            tone="glass"
            showCheck={false}
          />
          {categories.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <MoonFilterChip
                key={cat}
                label={
                  CATEGORY_I18N_KEYS[cat]
                    ? t(
                        `learn.encyclopedia_screens.conditions.categories.${CATEGORY_I18N_KEYS[cat]}`,
                      )
                    : cat
                }
                selected={isActive}
                onPress={() => setActiveCategory(isActive ? null : cat)}
                tone="glass"
                showCheck={false}
              />
            );
          })}
        </ScrollView>

        {/* Result count */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textFaint,
            marginBottom: 14,
          }}
        >
          {t(
            filtered.length === 1
              ? "learn.encyclopedia_screens.conditions.count_one"
              : "learn.encyclopedia_screens.conditions.count_other",
            { params: { count: String(filtered.length) } },
          )}
        </Text>

        {/* 2-column grid */}
        <View style={{ gap: 12 }}>
          {rows.map((row, rowIdx) => (
            <View key={rowIdx} style={{ flexDirection: "row", gap: 12 }}>
              {row.map((dx) => {
                const { body } = dx;
                return (
                  <Pressable
                    key={dx.id}
                    onPress={() => onItemPress(dx)}
                    style={{
                      flex: 1,
                      borderRadius: 22,
                      overflow: "hidden",
                      backgroundColor: "rgba(255, 255, 255, 0.4)",
                      borderWidth: 1,
                      borderColor: "rgba(255, 255, 255, 0.6)",
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: 4 },
                      shadowOpacity: 0.04,
                      shadowRadius: 16,
                      ...(Platform.OS === "web" &&
                        ({
                          backdropFilter: "blur(40px)",
                          WebkitBackdropFilter: "blur(40px)",
                        } as Record<string, string>)),
                    }}
                  >
                    {/* Icon */}
                    <View
                      style={{
                        alignItems: "center",
                        paddingTop: 24,
                        paddingBottom: 12,
                      }}
                    >
                      <View
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 24,
                          backgroundColor: "rgba(107, 85, 72, 0.08)",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Stethoscope
                          size={22}
                          color="#6B5548"
                          strokeWidth={1.5}
                        />
                      </View>
                    </View>

                    {/* Content */}
                    <View
                      style={{
                        padding: 16,
                        paddingTop: 4,
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 15,
                          fontWeight: "700",
                          color: EMBER.colors.text,
                          lineHeight: 20,
                          textAlign: "center",
                        }}
                        numberOfLines={2}
                      >
                        {dx.title}
                      </Text>

                      {/* Category tag */}
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          justifyContent: "center",
                          gap: 4,
                          marginTop: 4,
                        }}
                      >
                        <View
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            borderRadius: 8,
                            backgroundColor: "rgba(0,0,0,0.03)",
                            borderWidth: 1,
                            borderColor: "rgba(0,0,0,0.05)",
                          }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 10,
                              fontWeight: "500",
                              color: EMBER.colors.textMid,
                              letterSpacing: 0.2,
                            }}
                          >
                            {CATEGORY_I18N_KEYS[body.category]
                              ? t(
                                  `learn.encyclopedia_screens.conditions.categories.${CATEGORY_I18N_KEYS[body.category]}`,
                                )
                              : body.category}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </Pressable>
                );
              })}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          ))}
        </View>

        {/* Empty state */}
        {filtered.length === 0 && (
          <View style={{ alignItems: "center", paddingTop: 40 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                color: EMBER.colors.textLight,
                textAlign: "center",
              }}
            >
              {t("learn.encyclopedia_screens.conditions.empty")}
            </Text>
          </View>
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
